/**
 * Utility Storage Manager
 * Centralized localStorage management for utility partner data
 *
 * Replaces cookie-based storage with a single structured JSON object
 * Maintains backwards compatibility by reading from cookies as fallback
 */

import { CookieManager } from "@theme/cookie-manager";
import { isDev } from "@theme/logger";

/**
 * @typedef {Object} UtilityStorageData
 * @property {string} version - Storage schema version for future migrations
 * @property {PostalCodeData|null} postalCode - Postal code information
 * @property {UtilitySelectionData|null} utility - Selected utility information
 * @property {string|null} dismissedAt - ISO timestamp when user dismissed the modal
 * @property {number} [dismissedDays] - Cooldown period in days (e.g. 7 for no-matches flow)
 * @property {string|null} permanentlyDismissedAt - ISO timestamp when user permanently dismissed (legacy, kept for backward compat)
 * @property {CachedMatchesData|null} cachedMatches - Cached API matches for restore without network
 * @property {string} updatedAt - ISO timestamp of last update
 */

/**
 * @typedef {Object} CachedMatchesData
 * @property {string} postalCode - Postal code used for this lookup
 * @property {Array<Object>} matches - Enriched utility matches
 * @property {string} cachedAt - ISO timestamp when cached
 */

/**
 * @typedef {Object} PostalCodeData
 * @property {string} value - The postal/ZIP code (normalized, uppercase, no spaces)
 * @property {string} source - Where the postal code came from
 * @property {string} country - Country code (CA, US)
 * @property {string} confirmedAt - ISO timestamp when user confirmed this postal code
 */

/**
 * @typedef {Object} UtilitySelectionData
 * @property {string} code - Provider metaobject handle (canonical tag id; same as handle in v2)
 * @property {string} name - Provider display name
 * @property {string} region - Province/state code
 * @property {number} [rebateAmount] - Rebate amount in dollars
 * @property {string} [programHandle] - Selected program family handle
 * @property {string} [offerMode] - Selected program mysa_offer_mode
 * @property {string} selectedAt - ISO timestamp of selection
 */

export class UtilityStorage {
  static STORAGE_KEY = "mysa_utility_data";
  static VERSION = "1.0";
  static COOKIE_PREFIX = "mysa_utility";

  /**
   * Get all utility data from localStorage
   * Falls back to cookies if localStorage is empty (migration support)
   * @returns {UtilityStorageData|null}
   */
  static get() {
    try {
      // Try localStorage first
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (data) {
        return JSON.parse(data);
      }

      // Fallback to cookies for backwards compatibility
      return this.#migrateFromCookies();
    } catch (error) {
      console.error("Failed to read utility storage:", error);
      return null;
    }
  }

  /**
   * Save all utility data to localStorage
   * @param {UtilityStorageData} data
   */
  static set(data) {
    try {
      data.updatedAt = new Date().toISOString();
      data.version = this.VERSION;

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Failed to save utility storage:", error);
    }
  }

  /**
   * Get postal code data
   * @returns {PostalCodeData|null}
   */
  static getPostalCode() {
    const data = this.get();
    return data?.postalCode || null;
  }

  /**
   * Save postal code data
   * @param {string} postalCode - Postal/ZIP code
   * @param {string} source - Source of postal code ('user-confirmed'|'ip-geolocation')
   * @param {string} country - Country code
   */
  static setPostalCode(postalCode, source, country) {
    const data = this.get() || this.#createEmptyData();

    data.postalCode = {
      value: postalCode.replace(/\s/g, "").toUpperCase(),
      source,
      country,
      confirmedAt: new Date().toISOString(),
    };

    this.set(data);
  }

  /**
   * Get utility selection data
   * @returns {UtilitySelectionData|null}
   */
  static getUtility() {
    const data = this.get();
    return data?.utility || null;
  }

  /**
   * Save utility selection data
   * @param {string} code - Provider metaobject handle (canonical)
   * @param {string} name - Provider name
   * @param {string} region - Region/province code
   * @param {string} postalCode - User's postal code
   * @param {number} [rebateAmount] - Optional rebate amount
   * @param {string} [programHandle] - Optional selected program family handle
   * @param {string} [offerMode] - Optional selected program mysa_offer_mode
   * @param {string} [country] - Optional ISO country (CA/US) from provider catalog
   */
  static setUtility(
    code,
    name,
    region,
    postalCode,
    rebateAmount,
    programHandle,
    offerMode,
    country,
  ) {
    const data = this.get() || this.#createEmptyData();

    // Update postal code with user confirmation
    data.postalCode = {
      value: postalCode.replace(/\s/g, "").toUpperCase(),
      source: "user-confirmed",
      country:
        getCountryFromPostalCode(postalCode) ||
        country ||
        getCountryFromProviderCode(code),
      confirmedAt: new Date().toISOString(),
    };

    // Set utility selection
    data.utility = {
      code,
      name,
      region,
      rebateAmount,
      selectedAt: new Date().toISOString(),
    };

    if (programHandle) {
      data.utility.programHandle = programHandle;
    }

    if (offerMode) {
      data.utility.offerMode = offerMode;
    }

    this.set(data);
  }

  /**
   * Clear utility selection (keep postal code)
   */
  static clearUtility() {
    const data = this.get();
    if (!data) return;

    data.utility = null;
    this.set(data);
  }

  /**
   * Clear all data
   */
  static clear() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear utility storage:", error);
    }
  }

  /**
   * Check if user has confirmed a postal code
   * @returns {boolean}
   */
  static hasConfirmedPostalCode() {
    const postalCode = this.getPostalCode();
    return postalCode?.source === "user-confirmed";
  }

  /**
   * Check if user has selected a utility
   * @returns {boolean}
   */
  static hasUtilitySelection() {
    return this.getUtility() !== null;
  }

  /**
   * Mark the modal as dismissed by the user
   * Used to prevent auto-showing the modal again
   * Skipped in Shopify preview mode to allow testing (except when days > 1)
   * @param {number} [days=1] - Cooldown period in days (e.g. 7 for no-matches flow)
   */
  static setDismissed(days = 1) {
    // Skip 24h dismissal in preview mode; always persist longer cooldowns (e.g. 7-day no-matches)
    if (isDev() && days <= 1) return;

    const data = this.get() || this.#createEmptyData();
    data.dismissedAt = new Date().toISOString();
    data.dismissedDays = days;
    this.set(data);
  }

  /**
   * Check if user has dismissed the modal within the cooldown period
   * Uses stored dismissedDays when set, otherwise the passed days parameter
   * @param {number} days - Fallback days to check when no stored value (default: 1)
   * @returns {boolean}
   */
  static isDismissed(days = 1) {
    const data = this.get();

    // Legacy permanent dismissal — migrate to a 7-day cooldown so the flag
    // self-heals instead of creating an unrecoverable deadlock.
    if (data?.permanentlyDismissedAt) {
      data.dismissedAt = data.permanentlyDismissedAt;
      data.dismissedDays = 7;
      data.permanentlyDismissedAt = null;
      this.set(data);
    }

    if (!data?.dismissedAt) return false;

    const cooldownDays = data.dismissedDays ?? days;

    // In preview/dev mode, skip short cooldowns to allow testing
    if (isDev() && cooldownDays <= 1) return false;

    const dismissedDate = new Date(data.dismissedAt);
    const now = new Date();
    const diffMs = now.getTime() - dismissedDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return diffDays < cooldownDays;
  }

  /**
   * Clear the dismissed flag
   * Used when user explicitly opens the modal via trigger button
   */
  static clearDismissed() {
    const data = this.get();
    if (!data) return;

    data.dismissedAt = null;
    data.dismissedDays = null;
    data.permanentlyDismissedAt = null;
    this.set(data);
  }

  /**
   * Cache API matches for a postal code
   * Allows components to restore matches without making a new API call
   * @param {Array<Object>} matches - Enriched utility matches
   * @param {string} postalCode - Postal code used for lookup
   */
  static setLastMatches(matches, postalCode) {
    const data = this.get() || this.#createEmptyData();

    data.cachedMatches = {
      postalCode: postalCode.replace(/\s/g, "").toUpperCase(),
      matches,
      cachedAt: new Date().toISOString(),
    };

    this.set(data);
  }

  /**
   * Get cached API matches for a postal code
   * Returns null if no cache or if postal code doesn't match
   * @param {string} postalCode - Postal code to check
   * @returns {Array<Object>|null} Cached matches or null
   */
  static getLastMatches(postalCode) {
    const data = this.get();
    if (!data?.cachedMatches) return null;

    const normalizedPostal = postalCode.replace(/\s/g, "").toUpperCase();
    if (data.cachedMatches.postalCode !== normalizedPostal) return null;

    return data.cachedMatches.matches;
  }

  /**
   * Migrate data from cookies to localStorage
   * @returns {UtilityStorageData|null}
   */
  static #migrateFromCookies() {
    // Legacy utility cookies only. Never copy an IP hint into rebate storage.
    const partnerCode = CookieManager.get(`${this.COOKIE_PREFIX}_partner`);
    const postalCode = CookieManager.get(`${this.COOKIE_PREFIX}_postal`);

    if (!partnerCode && !postalCode) {
      return null;
    }

    const data = this.#createEmptyData();

    // Migrate postal code
    if (postalCode) {
      // If utility partner exists, postal code was user-confirmed
      // Otherwise, it's from IP geolocation
      const source = partnerCode ? "user-confirmed" : "ip-geolocation";

      data.postalCode = {
        value: postalCode.toUpperCase(),
        source,
        country: getCountryFromPostalCode(postalCode),
        confirmedAt: new Date().toISOString(),
      };
    }

    // Migrate utility selection
    if (partnerCode) {
      const partnerName =
        CookieManager.get(`${this.COOKIE_PREFIX}_partner_name`) || "";
      const region = CookieManager.get(`${this.COOKIE_PREFIX}_region`) || "";
      const rebateAmount = parseInt(
        CookieManager.get(`${this.COOKIE_PREFIX}_rebate`) || "0",
        10,
      );

      data.utility = {
        code: partnerCode,
        name: partnerName,
        region,
        rebateAmount: rebateAmount || undefined,
        selectedAt:
          CookieManager.get(`${this.COOKIE_PREFIX}_selected_at`) ||
          new Date().toISOString(),
      };
    }

    // Save migrated data to localStorage
    this.set(data);

    return data;
  }

  /**
   * Create empty data structure
   * @returns {UtilityStorageData}
   */
  static #createEmptyData() {
    return {
      version: this.VERSION,
      postalCode: null,
      utility: null,
      dismissedAt: null,
      permanentlyDismissedAt: null,
      cachedMatches: null,
      updatedAt: new Date().toISOString(),
    };
  }
}

/**
 * Legacy country inference from old region-style provider codes (`ca-*` / `us-*`).
 * v2 stores metaobject handles (e.g. `efficiencyone`) — prefer postal format or
 * `provider.country.code` from the catalog instead of this helper.
 *
 * @param {string} code - Legacy provider code or handle
 * @returns {string} 'CA', 'US', or '' when unknown
 */
export function getCountryFromProviderCode(code) {
  if (!code) return "";

  const normalized = code.toLowerCase();
  if (normalized.startsWith("ca-")) return "CA";
  if (normalized.startsWith("us-")) return "US";
  return "";
}

/**
 * Get country code from postal code format
 * Shared utility function to avoid duplication across modules
 *
 * @param {string} postalCode - Postal or ZIP code (with or without spaces)
 * @returns {string} 'CA' for Canadian, 'US' for American, '' for unknown
 *
 * @example
 * getCountryFromPostalCode('V6B 1A1') // 'CA'
 * getCountryFromPostalCode('90210')   // 'US'
 * getCountryFromPostalCode('12345-6789') // 'US'
 * getCountryFromPostalCode('')        // ''
 */
export function getCountryFromPostalCode(postalCode) {
  if (!postalCode) return "";

  const normalized = postalCode.replace(/\s/g, "").toUpperCase();

  // Canadian postal code format: A1A 1A1 or A1A1A1 (starts with letter-digit-letter)
  if (/^[A-Z]\d[A-Z]/.test(normalized)) {
    return "CA";
  }

  // US ZIP code format: 12345 or 12345-6789
  if (/^\d{5}(-\d{4})?$/.test(normalized)) {
    return "US";
  }

  return "";
}

// ============================================================================
// Visibility Helpers - toggle hidden attribute and .is-visible class
// ============================================================================

/**
 * Show an element by removing hidden attribute and adding is-visible class
 * @param {Element | null | undefined} el
 */
export function show(el) {
  if (!el) return;
  el.removeAttribute("hidden");
  el.classList.add("is-visible");
}

/**
 * Hide an element by adding hidden attribute and removing is-visible class
 * @param {Element | null | undefined} el
 * @param {Object} [opts={}]
 * @param {() => void} [opts.onComplete] - Optional callback after hiding
 */
export function hide(el, opts = {}) {
  if (!el) return;
  el.setAttribute("hidden", "");
  el.classList.remove("is-visible");
  if (opts.onComplete) opts.onComplete();
}
